// <CalendarWeek> — T-230 + T-231
// (time-view-week-implementation-plan.md §6.1 + §6.4 + §7.2-§8).
//
// Week-variant calendar body. Sibling to T-144's <CalendarTab>
// (Month variant). Fetches `GET /api/timeoff/calendar-week` (T-229)
// via React Query, keyed by the toolbar's URL state.
//
// T-230 shipped:
//   - Horizontal-scroll 7-day roster with the sticky employee column.
//   - Day-header row with weekday + numeric date.
//   - Empty / loading / error states.
//   - Wine-red `#A6264C` today highlight on the column header.
//
// T-231 adds:
//   - Stacking rule + "+N more" chip (in <CalendarWeekRow>).
//   - Auto-scroll-to-today on first render.
//   - "Hide weekends" — Sat/Sun cells dropped; Mon-Fri widen to 192px.
//
// Deferred to T-232:
//   - Empty-week banner, pixel polish, today's vertical line.

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CalendarWeekResponse } from '@pa/shared/time-view';
import { api, ApiError } from '../../api/client';
import CalendarWeekRow from './CalendarWeekRow';
import { useTimeViewUrlState } from './timeViewUrlState';

const STICKY_COL_W = 180;
const DAY_COL_W = 160;
const DAY_COL_W_WIDE = 192; // hide-weekends widens Mon-Fri.
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
// Indices of Sat (5) + Sun (6) in the Monday-anchored week.
const WEEKEND_INDICES = new Set<number>([5, 6]);
const ACCENT = '#A6264C';

/**
 * Returns the ISO `YYYY-MM-DD` for today in UTC. Mirrors the helper in
 * <CalendarTab> so the today calculations stay consistent across the
 * two variants.
 */
function todayUtcIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Build the 7 ISO day strings from a Monday-anchored `weekStart`. UTC
 * math so the bucket boundaries don't shift across DST.
 */
function buildWeekDays(weekStart: string): string[] {
  const start = new Date(`${weekStart}T00:00:00Z`);
  const days: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start.getTime());
    d.setUTCDate(d.getUTCDate() + i);
    days.push(d.toISOString().slice(0, 10));
  }
  return days;
}

/**
 * `<th>`-style header cell. Today's column gets the wine-red pill +
 * column tint that T-232 will continue polishing.
 */
function DayHeader({
  dayIso,
  label,
  isToday,
  isWeekend,
  widthPx,
}: {
  dayIso: string;
  label: string;
  isToday: boolean;
  isWeekend: boolean;
  widthPx: number;
}) {
  const dayNum = Number(dayIso.slice(8, 10));
  // T-232 §9 polish: row 1 `h-16` + circle day-number pill `w-7 h-7
  // rounded-full text-xs font-bold`. Sat/Sun (when hideWeekends is
  // OFF) get the subtler slate-50/80 tint.
  const bg = isToday
    ? 'bg-[#A6264C]/5'
    : isWeekend
      ? 'bg-slate-50/80'
      : 'bg-white';
  return (
    <div
      data-testid={`week-header-${dayIso}`}
      data-today={isToday ? 'true' : 'false'}
      className={`h-16 flex-none border-r border-slate-100 px-3 py-2 flex flex-col items-center justify-center gap-1 ${bg}`}
      style={{ width: widthPx }}
    >
      <span
        className={`text-[11px] font-bold uppercase tracking-wider ${
          isToday ? 'text-[#A6264C]' : 'text-slate-500'
        }`}
      >
        {label}
      </span>
      <span
        className={`text-xs font-bold inline-flex items-center justify-center w-7 h-7 rounded-full ${
          isToday ? 'text-white' : 'text-slate-800'
        }`}
        style={isToday ? { backgroundColor: ACCENT } : undefined}
      >
        {dayNum}
      </span>
    </div>
  );
}

/**
 * T-232 §9 — People's availability row above the body. Shows the
 * per-day count of out-employees + a 1.5px wine-red bar that grows
 * with the count. The maximum bar width is the day-cell width; the
 * bar is invisible when count === 0.
 */
function AvailabilityRow({
  availability,
  visibleDayIsos,
  todayIso,
  dayColW,
  maxOut,
}: {
  availability: Array<{ date: string; count: number }>;
  visibleDayIsos: string[];
  todayIso: string | null;
  dayColW: number;
  maxOut: number;
}) {
  const countByDay = new Map(availability.map((a) => [a.date, a.count]));
  return (
    <div
      data-testid="week-availability-row"
      className="flex border-b border-slate-100 bg-slate-50/60"
    >
      <div
        className="flex-none border-r border-slate-100 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 h-10 flex items-center"
        style={{ width: STICKY_COL_W }}
      >
        Out today
      </div>
      {visibleDayIsos.map((dayIso) => {
        const count = countByDay.get(dayIso) ?? 0;
        const barPct = maxOut > 0 ? (count / maxOut) * 100 : 0;
        const isToday = dayIso === todayIso;
        return (
          <div
            key={dayIso}
            data-testid={`week-availability-${dayIso}`}
            className={`flex-none border-r border-slate-100 px-2 h-10 flex items-center gap-2 ${
              isToday ? 'bg-[#A6264C]/5' : ''
            }`}
            style={{ width: dayColW }}
          >
            <span className="text-xs font-bold text-slate-700 tabular-nums w-5 text-right">
              {count}
            </span>
            <div className="flex-1 h-[1.5px] bg-slate-200 rounded overflow-hidden">
              {count > 0 && (
                <div
                  className="h-full bg-[#4B89FF]/80"
                  style={{ width: `${barPct}%` }}
                  aria-hidden
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function CalendarWeek() {
  const { state } = useTimeViewUrlState();
  const params = new URLSearchParams();
  // The Week endpoint takes any day in the week as anchor; the toolbar
  // already snaps the URL's `weekStart` to a Monday, so we pass it
  // through as the anchor.
  params.set('date', state.weekStart);
  if (state.hideWeekends) params.set('hideWeekends', 'true');
  if (state.site) params.set('siteId', state.site);
  if (state.team) params.set('teamId', state.team);
  if (state.type) params.set('requestType', state.type);

  const queryKey = [
    'timeoff',
    'calendar-week',
    state.weekStart,
    state.hideWeekends ? '1' : '0',
    state.site,
    state.team,
    state.type,
  ];

  const query = useQuery<CalendarWeekResponse>({
    queryKey,
    queryFn: () =>
      api.get<CalendarWeekResponse>(
        `/api/timeoff/calendar-week?${params.toString()}`,
      ),
  });

  // T-231 — auto-scroll today's column into view on first render. The
  // ref attaches to the horizontal-scroll container; we compute the
  // `scrollLeft` from the sticky-column width + the day-cell index.
  // Re-fires when `weekStart` changes (e.g., toolbar prev/next).
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!scrollRef.current) return;
    if (!query.data) return;
    const visibleDays = state.hideWeekends ? 5 : 7;
    const colW = state.hideWeekends ? DAY_COL_W_WIDE : DAY_COL_W;
    const today = todayUtcIso();
    const weekDays = buildWeekDays(query.data.weekStart);
    const filtered = state.hideWeekends
      ? weekDays.filter((_, i) => !WEEKEND_INDICES.has(i))
      : weekDays;
    const idx = filtered.indexOf(today);
    if (idx === -1 || idx < 1) return; // today's not visible or already at the left edge.
    // Center the today column-ish: scroll so today sits near the
    // viewport's left third.
    scrollRef.current.scrollLeft = idx * colW;
    void visibleDays; // (referenced for future hide-weekends-aware UX)
  }, [query.data, state.hideWeekends, state.weekStart]);

  if (query.isLoading) {
    return (
      <div
        data-testid="week-tab-loading"
        className="p-8 space-y-3"
      >
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="h-16 rounded-lg bg-slate-100 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (query.isError || !query.data) {
    // `/api/timeoff/calendar-week` is OWNER/ADMIN-only. A MEMBER who
    // lands here via direct URL gets a 403 — clarify that instead of
    // suggesting a refresh that wouldn't help.
    if (query.error instanceof ApiError && query.error.status === 403) {
      return (
        <div
          data-testid="week-tab-forbidden"
          className="p-8 text-sm text-slate-600"
        >
          <p className="font-medium text-slate-800 mb-1">
            People&rsquo;s time off is admin-only.
          </p>
          <p>
            Ask an OWNER or ADMIN in your organization for access. You
            can still see your own requests on the{' '}
            <a href="/time-off" className="text-[#6344C6] hover:underline">
              Time Off
            </a>{' '}
            page.
          </p>
        </div>
      );
    }
    return (
      <div data-testid="week-tab-error" className="p-8">
        <p className="text-rose-600 text-sm">
          Failed to load the calendar. Try refreshing the page.
        </p>
      </div>
    );
  }

  const { weekStart, rows, availability } = query.data;
  const allDays = buildWeekDays(weekStart);
  // T-231 — when hideWeekends is on, drop Sat (idx 5) + Sun (idx 6)
  // from BOTH the header and the row rendering. The endpoint still
  // returns 7-day buckets — we filter client-side so the API
  // contract stays simple.
  const visibleDayIndices = state.hideWeekends
    ? [0, 1, 2, 3, 4]
    : [0, 1, 2, 3, 4, 5, 6];
  const visibleDays = visibleDayIndices.map((i) => allDays[i]);
  const dayColW = state.hideWeekends ? DAY_COL_W_WIDE : DAY_COL_W;
  const todayIso = todayUtcIso();
  const todayInView = visibleDays.includes(todayIso);

  // T-232 §9 — max-out scaling for the availability bar.
  const maxOut = availability.reduce((m, a) => Math.max(m, a.count), 0);

  // T-232 §8 — empty-week banner. Triggered when the response has at
  // least one employee but NO entries anywhere in the visible week.
  const hasAnyEntry = rows.some((r) => r.entries.length > 0);
  const showEmptyBanner = rows.length > 0 && !hasAnyEntry;

  return (
    <div
      data-testid="calendar-week"
      data-hide-weekends={state.hideWeekends ? 'true' : 'false'}
      ref={scrollRef}
      className="overflow-x-auto"
    >
      {showEmptyBanner && (
        <div
          data-testid="week-empty-banner"
          className="px-8 py-2 text-xs text-slate-600 bg-slate-50 border-b border-slate-100"
        >
          No time off this week.
        </div>
      )}
      <div className="min-w-max">
        {/* Header row — always rendered so the chrome reads as a
            calendar even when the roster is empty. */}
        <div className="flex sticky top-0 bg-white z-10 border-b border-slate-200">
          <div
            className="flex-none border-r border-slate-100 px-4 h-16 text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center bg-white"
            style={{ width: STICKY_COL_W }}
          >
            Employee
          </div>
          {visibleDayIndices.map((i) => {
            const dayIso = allDays[i];
            const isWeekend = WEEKEND_INDICES.has(i);
            return (
              <DayHeader
                key={dayIso}
                dayIso={dayIso}
                label={DAY_LABELS[i]}
                isToday={dayIso === todayIso}
                isWeekend={isWeekend && !state.hideWeekends}
                widthPx={dayColW}
              />
            );
          })}
        </div>
        {/* T-232 §9 — People's availability row. */}
        <AvailabilityRow
          availability={availability}
          visibleDayIsos={visibleDays}
          todayIso={todayInView ? todayIso : null}
          dayColW={dayColW}
          maxOut={maxOut}
        />
        {rows.length === 0 ? (
          <div
            data-testid="week-tab-empty"
            className="p-8 text-sm text-slate-500"
          >
            No employees match the current filters.
          </div>
        ) : (
          rows.map((row) => (
            <CalendarWeekRow
              key={row.employeeId}
              row={row}
              weekDays={visibleDays}
              todayIso={todayInView ? todayIso : null}
              dayColWidth={dayColW}
            />
          ))
        )}
      </div>
    </div>
  );
}
